import { Dialog, formatDate, formatJson, Pre } from '@ijoin/components';
import { GetEnrollmentAnnuityPolicyRes } from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import {
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';

import { FC } from 'react';

import { Field } from './Field';

const DATE_TIME = { format: 'MM/DD/YYYY h:mm:ss A' } as const;
const DATE_ONLY_UTC = { format: 'MM/DD/YYYY', useUtc: true } as const;

export const EnrollmentAnnuityPolicyDetailsDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
  policy: GetEnrollmentAnnuityPolicyRes;
}> = props => (
  <Dialog
    data-testid='annuityPolicyDetails'
    fullWidth
    isOpen={props.isOpen}
    maxWidth='md'
    onClose={props.onClose}>
    <DialogTitle
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
      <Typography component='span' variant='h4'>
        {`${props.policy.annuityAccountType} Annuity Policy`}
      </Typography>
      <IconButton
        data-testid='closeAnnuityPolicyDetails'
        onClick={props.onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={3}>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Typography gutterBottom variant='overline'>
            Program
          </Typography>
          <Stack spacing={1}>
            <Field label='ID' value={props.policy.id} />
            <Field
              label='Account Type'
              value={props.policy.annuityAccountType}
            />
            <Field label='Status' value={props.policy.annuityProgramStatus} />
            <Field
              label='Origin'
              value={props.policy.annuityProgramOriginType}
            />
            <Field label='Product ID' value={props.policy.productId ?? ''} />
            <Field
              label='Annuity Integration ID'
              value={props.policy.annuityIntegrationId ?? ''}
            />
            <Field
              label='MAP Program ID'
              value={props.policy.managedAccountProgramId ?? ''}
            />
            <Field
              label='Traditional Account ID'
              value={props.policy.annuityTraditionalAccountId ?? ''}
            />
            <Field
              label='Roth Account ID'
              value={props.policy.annuityRothAccountId ?? ''}
            />
          </Stack>
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Typography gutterBottom variant='overline'>
            Dates
          </Typography>
          <Stack spacing={1}>
            <Field
              label='Entry Date'
              value={formatDate(
                props.policy.annuityProgramEntryDate,
                DATE_TIME
              )}
            />
            <Field
              label='Eligible On'
              value={formatDate(
                props.policy.eligibleForAccountOnDate,
                DATE_ONLY_UTC
              )}
            />
            <Field
              label='Initial Funding'
              value={
                props.policy.initialFundingDate
                  ? formatDate(props.policy.initialFundingDate, DATE_ONLY_UTC)
                  : ''
              }
            />
            <Field
              label='Last Sweep'
              value={
                props.policy.lastSweepDate
                  ? formatDate(props.policy.lastSweepDate, DATE_ONLY_UTC)
                  : ''
              }
            />
            <Field
              label='Next Sweep'
              value={
                props.policy.nextSweepDate
                  ? formatDate(props.policy.nextSweepDate, DATE_ONLY_UTC)
                  : ''
              }
            />
            <Field
              label='Exit Date'
              value={
                props.policy.annuityProgramExitDate
                  ? formatDate(props.policy.annuityProgramExitDate, DATE_TIME)
                  : ''
              }
            />
            <Field
              label='Exit Reason'
              value={props.policy.annuityProgramExitReason ?? ''}
            />
            <Field
              label='Created'
              value={
                props.policy.createdAt
                  ? formatDate(props.policy.createdAt, DATE_TIME)
                  : ''
              }
            />
            <Field
              label='Updated'
              value={
                props.policy.updatedAt
                  ? formatDate(props.policy.updatedAt, DATE_TIME)
                  : ''
              }
            />
          </Stack>
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Typography gutterBottom variant='overline'>
            Sweeps
          </Typography>
          <Stack spacing={1}>
            <Field
              label='Current Sweep %'
              value={props.policy.currentSweepPercentage ?? ''}
            />
            <Field
              label='Initial Sweep % of Bonds'
              value={props.policy.initialSweepAsPercentOfBonds ?? ''}
            />
            <Field
              label='Initial Sweep % of Total'
              value={props.policy.initialSweepAsPercentOfTotal ?? ''}
            />
          </Stack>
        </Grid>
      </Grid>

      <Typography gutterBottom sx={{ mt: 3 }} variant='overline'>
        Last Sweep Details
      </Typography>
      <Paper variant='outlined'>
        <Pre>
          {props.policy.lastSweepDetails
            ? formatJson(props.policy.lastSweepDetails)
            : 'No sweep details'}
        </Pre>
      </Paper>

      <Typography gutterBottom sx={{ mt: 3 }} variant='overline'>
        Annuity Account Details
      </Typography>
      <Paper variant='outlined'>
        <Pre>
          {props.policy.annuityAccountDetails
            ? formatJson(props.policy.annuityAccountDetails)
            : 'No account details'}
        </Pre>
      </Paper>

      <Typography gutterBottom sx={{ mt: 3 }} variant='overline'>
        Annuity Index Allocations
      </Typography>
      <Paper variant='outlined'>
        <Pre>
          {props.policy.annuityIndexAllocations
            ? formatJson(props.policy.annuityIndexAllocations)
            : 'No index allocations'}
        </Pre>
      </Paper>
    </DialogContent>
  </Dialog>
);

EnrollmentAnnuityPolicyDetailsDialog.displayName =
  'EnrollmentAnnuityPolicyDetailsDialog';
